import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { mvteApiInstance } from "../mvte.api";
import { ExamType } from "src/apps/driver-road-test/types/exam-type";
import { Section } from "src/apps/driver-road-test/types/section";
import { SavedExam } from "src/apps/driver-road-test/types/exam";

type ApiSuccessResponse<T> = {
    status: "success";
    data: T;
};

type ApiErrorResponse = {
    status: "error";
    message: string;
};

type ApiValidationFailure = {
    status: "fail";
    data: Record<string, unknown>;
};

// Combined generic type
type ApiResponse<T> =
    | ApiSuccessResponse<T>
    | ApiErrorResponse
    | ApiValidationFailure;

type GetTenstreetApplicantResponse = ApiResponse<{
    tenstreetApplicant: TenstreetApplicant;
}>;

async function getTenstreetApplicant(data: {
    params: { licenseNumber: string };
}) {
    const response = await mvteApiInstance.get<GetTenstreetApplicantResponse>(
        `/driver-road-test/tenstret-applicants`,
        {
            params: {
                licenseNumber: data.params.licenseNumber,
            },
        },
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.tenstreetApplicant;
}

type GetDriverExamsResponse = ApiResponse<{
    driverExams: SavedExam[];
}>;

async function getDriverExams(data: { params: { licenseNumber: string } }) {
    const response = await mvteApiInstance.get<GetDriverExamsResponse>(
        `/driver-road-test/exams`,
        {
            params: {
                licenseNumber: data.params.licenseNumber,
            },
        },
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.driverExams;
}

type GetExamTypesResponse = ApiResponse<{
    examTypes: ExamType[];
}>;

async function getExamTypes() {
    const response = await mvteApiInstance.get<GetExamTypesResponse>(
        `/driver-road-test/exam-types`,
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.examTypes;
}

type GetSectionsWithQuestionsResponse = ApiResponse<{
    sectionsWithQuestions: Section[];
}>;

async function getSectionsWithQuestions() {
    const response =
        await mvteApiInstance.get<GetSectionsWithQuestionsResponse>(
            `/driver-road-test/sections-and-questions`,
        );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.sectionsWithQuestions;
}

type CreateExamResponse = ApiResponse<{
    createdExam: SavedExam;
}>;

async function createExam(data: {
    body: {
        exam: {
            licenseNumber: string;
            examType: string;
            examinerEmployeeCode: string;
        };
    };
}) {
    try {
        const response = await mvteApiInstance.post<CreateExamResponse>(
            `/driver-road-test/exams`,
            {
                licenseNumber: data.body.exam.licenseNumber,
                examType: data.body.exam.examType,
                examinerEmployeeCode: data.body.exam.examinerEmployeeCode,
            },
        );

        if (response.data.status === "fail") {
            throw new Error(JSON.stringify(response.data));
        }

        if (response.data.status === "error") {
            throw new Error(response.data.message);
        }

        return response.data.data.createdExam;
    } catch (err) {
        console.error("Error saving exam:", err);
        throw err;
    }
}

type UpdateExamResponse = ApiResponse<{
    exam: SavedExam;
}>;

async function updateExam(data: {
    body: {
        exam: SavedExam;
    };
}) {
    const response = await mvteApiInstance.post<UpdateExamResponse>(
        `/driver-road-test/exams/${data.body.exam.examId}`,
        data.body.exam,
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.exam;
}

type CompleteExamResponse = ApiResponse<{
    completedExam: SavedExam;
}>;

async function completeExam(data: {
    body: {
        exam: SavedExam;
        driver: TenstreetApplicant;
        signatureImageBase64: string;
    };
}) {
    const response = await mvteApiInstance.post<CompleteExamResponse>(
        `/driver-road-test/exams/complete`,
        data.body,
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.completedExam;
}

type GetCertificateResponse = ApiResponse<{
    certificateBase64: string;
}>;

async function getCertificate(data: { params: { certificateName: string } }) {
    const response = await mvteApiInstance.get<GetCertificateResponse>(
        `/driver-road-test/certificates?certificateName=${data.params.certificateName}`,
    );

    if (response.data.status === "fail") {
        throw new Error(JSON.stringify(response.data));
    }

    if (response.data.status === "error") {
        throw new Error(response.data.message);
    }

    return response.data.data.certificateBase64;
}

const driverRoadTestApi = {
    getTenstreetApplicant,
    getDriverExams,
    getExamTypes,
    getSectionsWithQuestions,
    createExam,
    updateExam,
    completeExam,
    getCertificate,
};

export { driverRoadTestApi };
